import { TabPanel, TabPanels, Tabs } from '@reach/tabs';
import { withExperiments } from '@wix/wix-experiments-react';
import classNames from 'classnames';
import { PlanType } from 'enums';
import { observer } from 'mobx-react';
import { withInjectedRootStore } from 'providers';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import * as React from 'react';
import { withGtmLogger } from '../../components/HOCs/withGtmLogger';
import { RequestCallModal } from '../../components/RequestCall/Modal/RequestCallModal';
import { plansPageTabs } from '../../constants';
import PlansGrid from './AdvancedPlans/PlansGrid';
import { GridWrapper } from './AdvancedPlans/PlansGrid/GridWrapper';
import { Seperator } from './AdvancedPlans/Seperator/Seperator';
import { PlansPageBottomBanner } from './BottomBanners';
import { EnterpriseTab } from './Enterprise/EnterpriseTab';
import { PlansPageFooter } from './Footer/PlansPageFooter';
import { LimitedTimeBanner } from './LimitedTimeBanner/LimitedTimeBanner';
import { NoTabsBanner } from './PaymentBanners';
import { usePlansPageController } from './PlansPage.controller';
import s from './PlansPage.scss';
import { PlansPageProps } from './PlansPageProps';
import { TabButtons } from './Tabs/TabButtons';
import { TabPanelTransition } from './Tabs/TabPanelTransition';
import { useGetProducts } from './shared/useGetProducts';

const PlansPageComp: React.FC<PlansPageProps> = (props) => {
  const controller = usePlansPageController(props);
  const {
    features,
    businessFeatures,
    sections,
    websiteFeatureGroups,
    businessFeatureGroups,
  } = props.rootStore.appStore.appContext;
  const {
    planType,
    shouldStoreIntentNoTabs,
    shouldShowTabs,
    jumpToTab,
    isInSale,
  } = props.rootStore.appStore;
  const {
    shouldShowLimitedTimeBanner,
    websiteBannerCTA,
    isRequestACallModalOpened,
    closeRequestACallModal,
    onSelectProduct,
    hasEnterpriseProduct,
  } = controller;

  const { websiteProducts, businessProducts } = useGetProducts({
    appStore: props.rootStore.appStore,
  });

  const handleTabChange = (index: number) => {
    const { selectTab } = props.rootStore.appStore;
    selectTab(plansPageTabs[index]);
  };

  const { theme } = usePageModeContext();
  return (
    <div
      data-testid='advanced-plans-page'
      className={classNames(props.className, s.root, theme, {
        [s.enterprise]: planType === PlanType.Enterprise,
        [s.sale]: isInSale,
      })}
    >
      <Tabs
        onChange={handleTabChange}
        defaultIndex={plansPageTabs.indexOf(planType)}
        index={plansPageTabs.indexOf(planType)}
      >
        {shouldShowTabs && (
          <GridWrapper>
            <TabButtons />
          </GridWrapper>
        )}
        <TabPanels>
          <TabPanelTransition predicate={planType === PlanType.Website}>
            <TabPanel data-testid='website-tab' className={s.tabPanel}>
              {planType === PlanType.Website && (
                <GridWrapper>
                  {shouldStoreIntentNoTabs && (
                    <NoTabsBanner action={jumpToTab} />
                  )}
                  <PlansGrid
                    onClickSelect={onSelectProduct}
                    features={features}
                    products={websiteProducts}
                    sections={[]}
                    groups={websiteFeatureGroups}
                  />
                </GridWrapper>
              )}
              {shouldShowTabs ? (
                <PlansPageBottomBanner
                  onClickHandler={websiteBannerCTA}
                  hasEnterpriseProduct={hasEnterpriseProduct}
                />
              ) : (
                <Seperator />
              )}
            </TabPanel>
          </TabPanelTransition>
          <TabPanelTransition predicate={planType === PlanType.Business}>
            <TabPanel data-testid='business-tab' className={s.tabPanel}>
              {planType === PlanType.Business && (
                <>
                  {shouldShowLimitedTimeBanner && <LimitedTimeBanner />}
                  {shouldStoreIntentNoTabs && (
                    <NoTabsBanner action={jumpToTab} />
                  )}
                  <GridWrapper>
                    <PlansGrid
                      onClickSelect={onSelectProduct}
                      features={businessFeatures ?? features}
                      products={businessProducts}
                      sections={sections}
                      groups={businessFeatureGroups}
                    />
                  </GridWrapper>
                  <PlansPageBottomBanner
                    onClickHandler={websiteBannerCTA}
                    hasEnterpriseProduct={hasEnterpriseProduct}
                  />
                </>
              )}
            </TabPanel>
          </TabPanelTransition>
          <TabPanelTransition predicate={planType === PlanType.Enterprise}>
            <TabPanel data-testid='enterprise-tab' className={s.tabPanel}>
              {planType === PlanType.Enterprise && <EnterpriseTab />}
            </TabPanel>
          </TabPanelTransition>
        </TabPanels>
      </Tabs>

      {planType !== PlanType.Enterprise && (
        <GridWrapper>
          <PlansPageFooter />
        </GridWrapper>
      )}
      <RequestCallModal
        onCancelClick={closeRequestACallModal}
        isOpen={isRequestACallModalOpened}
      />
    </div>
  );
};

export const PlansPage = withGtmLogger(
  withExperiments(withInjectedRootStore(observer(PlansPageComp))),
);
